import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Plus, Car } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { toast } from "sonner";

const STATUS = [
  { key: "free", label: "Volný" },
  { key: "rented", label: "Pronajatý" },
  { key: "service", label: "Servis" },
];
const empty = { brand: "", model: "", license_plate: "", vin: "", status: "free" };

const AddCarModal = ({ token, onClose, onDone }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand.trim() || !form.model.trim()) { toast.error("Vyplňte značku a model."); return; }
    setSaving(true);
    try {
      await apiFetch("/admin/cars", { method: "POST", token, body: { ...form } });
      toast.success(`Vůz ${form.brand} ${form.model} přidán do flotily.`);
      if (keepOpen) {
        // keep brand/model for quickly adding another identical car with a new SPZ/VIN
        setForm((f) => ({ ...f, license_plate: "", vin: "" }));
        onDone(false);
      } else {
        onDone(true);
      }
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="add-car-modal">
      <div className="fl-card rounded-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 fl-divider">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold inline-flex items-center gap-2"><Car className="w-5 h-5 fl-accent-text" /> Přidat vůz do flotily</h3>
          <button onClick={onClose} className="fl-muted hover:opacity-80" data-testid="add-car-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3" data-testid="add-car-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Značka</span>
              <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="fl-input w-full" placeholder="např. Škoda" required data-testid="add-car-brand" /></label>
            <label className="block"><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Model</span>
              <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="fl-input w-full" placeholder="např. Octavia TS" required data-testid="add-car-model" /></label>
            <label className="block"><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">SPZ / RZ</span>
              <input value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} className="fl-input w-full font-mono" placeholder="volitelné" data-testid="add-car-plate" /></label>
            <label className="block"><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">VIN</span>
              <input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="fl-input w-full font-mono" placeholder="volitelné" data-testid="add-car-vin" /></label>
          </div>
          <label className="block"><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Stav</span>
            <div className="flex gap-2" data-testid="add-car-status">
              {STATUS.map((o) => (
                <button key={o.key} type="button" onClick={() => setForm({ ...form, status: o.key })}
                  className={`flex-1 text-sm py-2 rounded-xl border transition-colors ${form.status === o.key ? "fl-accent-soft fl-accent-border" : "fl-border fl-muted hover:opacity-80"}`}>{o.label}</button>
              ))}
            </div>
          </label>
          <p className="text-[11px] fl-muted">Máte víc stejných aut? Zaškrtněte níže a přidávejte je hned po sobě – každé dostane vlastní SPZ/VIN.</p>
          <label className="flex items-center gap-2 text-sm fl-text-2"><input type="checkbox" checked={keepOpen} onChange={(e) => setKeepOpen(e.target.checked)} data-testid="add-car-keepopen" /> Přidat další stejné auto po uložení</label>
          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="text-sm fl-muted hover:opacity-80" data-testid="add-car-cancel">Zrušit</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full fl-accent-bg font-semibold disabled:opacity-60" data-testid="add-car-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Přidat vůz</button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddCarModal;
